import { useState, useEffect } from 'react';
import { ArrowRight, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { scrollToHash } from '@/lib/scroll';
import { LogoCarousel } from './LogoCarousel';

const headlines = ["Government Agencies", "Growing Businesses", "Maximum Impact"];

export function Hero() {
  const [headlineIndex, setHeadlineIndex] = useState(0);

  useEffect(() => {
    // Rotate the headline word. Entrance animations are pure CSS (see
    // index.css) so this component no longer pulls in framer-motion, keeping
    // it out of the initial bundle.
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % headlines.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    scrollToHash(href);
  };

  return (
    <section className="relative min-h-[100svh] flex flex-col overflow-hidden">
      {/* Momentum Bar - Shoots across on load */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] z-50 momentum-bar"
        style={{
          background: 'linear-gradient(90deg, hsl(var(--gold)) 0%, hsl(var(--gold-light)) 60%, transparent 100%)',
          animation: 'momentum-shoot 0.6s ease-out forwards',
          transform: 'scaleX(0)',
          transformOrigin: 'left center',
          willChange: 'transform, opacity',
        }}
        aria-hidden="true"
      />

      {/* Kinetic Background */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {/* Deep navy gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep via-navy-deep to-navy" />

        {/* Animated grid lines */}
        <div
          className="absolute inset-0 flow-lines"
          style={{
            background: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 80px,
              rgba(178, 117, 53, 0.04) 80px,
              rgba(178, 117, 53, 0.04) 81px
            )`,
            animation: 'grid-pulse 0.5s ease-out',
            opacity: 0.5,
          }}
        />

        {/* Floating ambient glow */}
        <div
          className="absolute inset-0 progress-pulse hidden md:block"
          style={{
            background: `
              radial-gradient(circle at 20% 30%, rgba(178,117,53,0.08) 0%, transparent 40%),
              radial-gradient(circle at 80% 60%, rgba(157,195,221,0.06) 0%, transparent 40%)
            `,
            animation: 'pulse-drift 8s ease-in-out infinite',
          }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        <div className="container mx-auto px-5 sm:px-6 py-16 sm:py-20 md:py-24 text-center max-w-full">
          {/* Eyebrow — paints immediately for fast FCP */}
          <div className="mb-6">
            <span className="inline-block text-xs tracking-[0.2em] uppercase text-teal-light">
              Precision Advertising That Delivers
            </span>
          </div>

          {/* Main Headline — paints immediately; only the rotating word animates */}
          <div className="mb-5 sm:mb-6">
            <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white leading-normal tracking-tight max-w-4xl mx-auto overflow-visible">
              <span className="block sm:inline">Digital Marketing for</span>{' '}
              <span className="inline-block relative overflow-visible whitespace-nowrap">
                <span
                  key={headlines[headlineIndex]}
                  className="word-rotate-in font-medium text-gold-bright"
                  style={{ lineHeight: 1.4, paddingBottom: '0.15em' }}
                >
                  {headlines[headlineIndex]}
                </span>
              </span>
            </h1>
          </div>

          {/* Subheadline — LCP element; render eagerly without JS-gated opacity */}
          <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed">
            Launch targeted campaigns in 48 hours. Access premium broadcast inventory
            at accessible rates. Get concierge-level service from day one.
          </p>

          {/* CTAs */}
          <div
            className="hero-rise flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto mb-8 sm:mb-10"
            style={{ animationDelay: '1.1s' }}
          >
            {/* Primary CTA with arrow animation */}
            <Button
              asChild
              size="lg"
              className="group relative bg-gold hover:bg-[hsl(30_60%_30%)] text-white font-semibold px-8 py-6 text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-gold/25 w-full sm:w-auto overflow-visible"
            >
              <Link to="/contact">
                <span className="relative z-10 flex items-center gap-2">
                  Start Your Campaign
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
                {/* Progress ring on hover */}
                <span className="absolute inset-[-3px] rounded-lg border-2 border-transparent group-hover:border-gold-light opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:animate-[border-progress_0.6s_ease-out]" />
              </Link>
            </Button>

            <Button
              asChild
              variant="outline"
              size="lg"
              className="border border-teal-light/40 text-teal-light bg-transparent hover:bg-white/5 font-semibold px-8 py-6 text-sm tracking-wide uppercase transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
            >
              <a href="#solutions" onClick={(e) => handleNavClick(e, '#solutions')}>
                See How We Help
              </a>
            </Button>
          </div>

          {/* Velocity Badges */}
          <div
            className="hero-rise flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-10 pt-6 sm:pt-8 border-t border-gold/20"
            style={{ animationDelay: '1.4s' }}
          >
            {/* Fast Launch */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/15">
                <Zap className="w-5 h-5 text-gold-bright" />
              </div>
              <div className="text-left">
                <div className="text-xl font-semibold text-gold-bright font-mono tracking-tight">48hr</div>
                <div className="text-xs uppercase tracking-widest text-white/70">Launch Ready</div>
              </div>
            </div>

            {/* Low Entry with Live indicator */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="live-indicator w-3 h-3 rounded-full bg-emerald-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-semibold text-gold-bright font-mono tracking-tight">$500</div>
                <div className="text-xs uppercase tracking-widest text-white/70">Entry Point</div>
              </div>
            </div>

            {/* Precision Targeting */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/15">
                <Target className="w-5 h-5 text-gold-bright" />
              </div>
              <div className="text-left">
                <div className="text-xl font-semibold text-gold-bright font-mono tracking-tight">1000+</div>
                <div className="text-xs uppercase tracking-widest text-white/70">Targeting Signals</div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div
            className="hero-rise flex items-center justify-center gap-3 mt-6 sm:mt-10 text-sm text-white/70"
            style={{ animationDelay: '1.6s' }}
          >
            <span>Orlando, Florida</span>
            <span
              className="line-grow w-8 h-px bg-gold"
              style={{ animationDelay: '1.8s' }}
              aria-hidden="true"
            />
            <span
              className="dot-pulse w-2 h-2 rounded-full bg-gold"
              style={{ animationDelay: '2s' }}
              aria-hidden="true"
            />
            <span>Nationwide Reach</span>
          </div>
        </div>
      </div>

      {/* Logo Carousel */}
      <div className="relative z-10 bg-navy-deep">
        <LogoCarousel />
      </div>
    </section>
  );
}
